import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Popover,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/hooks';
import type { FlagData } from '../TimeLineBlock/types';

import type { FlagPopoverProps } from './types';

import './FlagPopover.css';

const REQUIRED_ASTERISK = '*';

/**
 * FlagPopoverContent Component
 * Content for the flag popover
 */
interface FlagPopoverContentProps {
  onClose: () => void;
  onSave: (comment: string) => Promise<void>;
  onResolve?: (comment: string) => Promise<void>;
  flagData?: FlagData;
  onViewAllActivities?: () => void;
  initialResolve?: boolean;
  commentOnly?: boolean;
}

function FlagPopoverContent({
  onClose,
  onSave,
  onResolve,
  flagData,
  onViewAllActivities,
  initialResolve = false,
  commentOnly = false,
}: FlagPopoverContentProps): React.ReactElement {
  const l = useInvertedLocalize();
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [showResolveForm, setShowResolveForm] = useState<boolean>(false);

  const flagStatus = flagData?.status ?? 'none';
  const isResolvedFlag = flagStatus === 'resolved';

  const handleSave = () => {
    if (!comment.trim()) {
      return;
    }

    setIsSubmitting(true);

    const savePromise = onSave(comment.trim());

    savePromise
      .then(() => {
        setComment('');
        onClose();
      })
      .catch(() => {
        // Error handling - could show toast/notification
        // NOTE: Error logging will be implemented when proper error handling is added
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleResolve = () => {
    if (!onResolve) {
      return;
    }

    setIsResolving(true);

    const resolvePromise = onResolve(comment.trim());

    resolvePromise
      .then(() => {
        onClose();
      })
      .catch(() => {
        // NOTE: Error logging will be implemented when proper error handling is added
      })
      .finally(() => {
        setIsResolving(false);
      });
  };

  const handleCancel = () => {
    setComment(''); // Clear form on cancel
    setShowAddComment(false); // Reset add comment state
    onClose();
  };

  // removed unused back handler after simplifying cancel behavior

  const handleAddComment = () => {
    // For needs_resolution flags, transition to new flag UI to add comment
    setShowAddComment(true);
  };

  // Removed legacy onChange handler in favor of setValue directly on InputField

  // When asked to start in resolve form (timeline), open resolve form directly for pending review
  React.useEffect(() => {
    if (initialResolve && flagStatus === 'needs_resolution') {
      setShowResolveForm(true);
    }
  }, [initialResolve, flagStatus]);

  // Comment-only variant (third variant) OR when user clicks Comment inside FLAGGED view
  if (commentOnly || showAddComment) {
    return (
      <Box className='FlagPopover-content'>
        <Box className='FlagPopover-header'>
          <Box className='FlagPopover-headerContent'>
            <Icon
              kind='Comment'
              size='medium'
              className='FlagPopover-flagIcon'
            />
            <Box className='FlagPopover-headerText'>
              <Box className='FlagPopover-titleRow'>
                <Typography kind='h4' className='FlagPopover-title'>
                  {l('Add Comments')}
                </Typography>
                <ButtonIcon
                  kind='ghost'
                  size='small'
                  ariaLabel={l('close')}
                  icon={<Icon kind='Close' size='small' />}
                  onClick={onClose}
                  className='FlagPopover-closeButton'
                />
              </Box>
              <Typography kind='body2' className='FlagPopover-description'>
                {l(
                  'Add a note to this log item without flagging it. Use this to document context, questions, or general observations during review.'
                )}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className='FlagPopover-form'>
          <Box className='FlagPopover-commentField'>
            <Typography kind='h4'>
              <span style={{ color: 'red' }}>{REQUIRED_ASTERISK}</span>{' '}
              {l('Comments')}
            </Typography>
            <textarea
              name='commentOnly'
              aria-label={l('Comments')}
              placeholder={l('Write your comment')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              required
            />
          </Box>
        </Box>

        <Box className='FlagPopover-buttons'>
          <Button
            kind='outlined'
            size='medium'
            onClick={onClose}
            isDisabled={isSubmitting}>
            {l('Cancel')}
          </Button>
          <Button
            kind='filled'
            size='medium'
            onClick={handleSave}
            isDisabled={comment.trim().length <= 2 || isSubmitting}>
            {isSubmitting ? l('Saving') : l('Save')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Show resolved flag UI when resolved
  if (isResolvedFlag) {
    return (
      <Box className='FlagPopover-content'>
        {/* Header Section */}
        <Box className='FlagPopover-header'>
          <Box className='FlagPopover-headerContent'>
            <Icon
              kind='FlagResolved'
              size='medium'
              className='FlagPopover-flagIcon'
            />
            <Box className='FlagPopover-headerText'>
              <Box className='FlagPopover-titleRow'>
                <Typography
                  lineLimit={3}
                  kind='h4'
                  className='FlagPopover-title'>
                  {l('Flagged and then resolved')}
                </Typography>
                <ButtonIcon
                  kind='ghost'
                  size='small'
                  ariaLabel={l('close')}
                  icon={<Icon kind='Close' size='small' />}
                  onClick={onClose}
                  className='FlagPopover-closeButton'
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Resolved by info */}
        <Box className='FlagPopover-resolvedBy'>
          <Typography
            lineLimit={3}
            kind='body3'
            className='FlagPopover-resolvedByLabel'>
            {l('Resolved by')}
          </Typography>
          <Typography
            lineLimit={3}
            kind='body2'
            className='FlagPopover-resolvedByName'>
            {(() => {
              const nameRaw = flagData?.flaggedByName;
              const idRaw = flagData?.flaggedBy;
              const name =
                typeof nameRaw === 'string' && nameRaw.trim().length > 0
                  ? nameRaw
                  : undefined;
              const id =
                typeof idRaw === 'string' && idRaw.trim().length > 0
                  ? idRaw
                  : undefined;

              if (name && id) {
                return `${name} (${id})`;
              }

              if (name) {
                return name;
              }

              if (id) {
                return id;
              }

              return '-';
            })()}
          </Typography>
        </Box>

        {/* Resolution comment */}
        {flagData?.comment && (
          <Box className='FlagPopover-resolutionComment'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='FlagPopover-commentLabel'>
              {l('Comments')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='FlagPopover-commentText'>
              {flagData.comment}
            </Typography>
          </Box>
        )}

        {/* Actions: allow adding a comment even when resolved */}
        <Box className='FlagPopover-buttons'>
          <Button
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Comment' size='small' />}
            onClick={() => setShowAddComment(true)}>
            {l('Comment')}
          </Button>
          <Button
            kind='outlined'
            size='medium'
            rightIcon={<Icon kind='ArrowRight' size='small' />}
            onClick={() => {
              onViewAllActivities?.();
              onClose();
            }}>
            {l('View All Activities')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Show needs_resolution flag UI (but not when showing add comment form)
  if (flagStatus === 'needs_resolution' && !showResolveForm) {
    return (
      <Box className='FlagPopover-content'>
        {/* Header Section */}
        <Box className='FlagPopover-header'>
          <Box className='FlagPopover-headerContent'>
            <Icon
              kind='FlagFilled'
              size='medium'
              className='FlagPopover-flagIcon'
            />
            <Box className='FlagPopover-headerText'>
              <Box className='FlagPopover-titleRow'>
                <Typography
                  lineLimit={3}
                  kind='h4'
                  className='FlagPopover-title'>
                  {l('Flagged')}
                </Typography>
                <ButtonIcon
                  kind='ghost'
                  size='small'
                  ariaLabel={l('close')}
                  icon={<Icon kind='Close' size='small' />}
                  onClick={onClose}
                  className='FlagPopover-closeButton'
                />
              </Box>
              <Typography
                lineLimit={3}
                kind='body2'
                className='FlagPopover-description'>
                {l(
                  'This item was flagged for investigation. To complete the review, resolve the flag and explain how the issue was handled.'
                )}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Flagged by info */}
        {flagData?.flaggedByName && (
          <Box className='FlagPopover-flaggedBy'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='FlagPopover-flaggedByLabel'>
              {l('Flagged by')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='FlagPopover-flaggedByName'>
              {flagData.flaggedBy
                ? `${flagData.flaggedByName} (${flagData.flaggedBy})`
                : flagData.flaggedByName}
            </Typography>
          </Box>
        )}

        {/* Existing comment */}
        {flagData?.comment && (
          <Box className='FlagPopover-existingComment'>
            <Typography
              lineLimit={3}
              kind='body3'
              className='FlagPopover-commentLabel'>
              {l('Comments')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='FlagPopover-commentText'>
              {flagData.comment}
            </Typography>
          </Box>
        )}

        {/* Button Section */}
        <Box className='FlagPopover-buttons'>
          <Button
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Comment' size='small' />}
            onClick={handleAddComment}
            isDisabled={isSubmitting || isResolving}>
            {l('Comment')}
          </Button>
          <Button
            kind='filled'
            size='medium'
            onClick={() => setShowResolveForm(true)}
            isDisabled={isResolving || isSubmitting}
            leftIcon={<Icon kind='FlagResolved' color='gray-000' />}>
            {isResolving ? l('Resolving') : l('Resolve Flag')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Resolve form for timeline and detail panel
  if (flagStatus === 'needs_resolution' && showResolveForm) {
    return (
      <Box className='FlagPopover-content'>
        {/* Header Section */}
        <Box className='FlagPopover-header'>
          <Box className='FlagPopover-headerContent'>
            <Icon
              kind='FlagResolved'
              size='medium'
              className='FlagPopover-flagIcon'
            />
            <Box className='FlagPopover-headerText'>
              <Box className='FlagPopover-titleRow'>
                <Typography
                  lineLimit={3}
                  kind='h4'
                  className='FlagPopover-title'>
                  {l('Resolve Flag')}
                </Typography>
                <ButtonIcon
                  kind='ghost'
                  size='small'
                  ariaLabel={l('close')}
                  icon={<Icon kind='Close' size='small' />}
                  onClick={onClose}
                  className='FlagPopover-closeButton'
                />
              </Box>
              <Typography
                lineLimit={3}
                kind='body2'
                className='FlagPopover-description'>
                {l(
                  'To resolve this issue and remove the flag, provide a brief explanation of what was done to address it.'
                )}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Form Section */}
        <Box className='FlagPopover-form'>
          <Box className='FlagPopover-commentField'>
            <Typography kind='h4'>
              <span style={{ color: 'red' }}>{REQUIRED_ASTERISK}</span>{' '}
              {l('Comments')}
            </Typography>
            <textarea
              name='resolveComment'
              aria-label={l('Comments')}
              placeholder={l('Add a brief explanation for resolution.')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onKeyUp={(e) => {
                e.stopPropagation();
              }}
              required
            />
          </Box>
        </Box>

        {/* Button Section */}
        <Box className='FlagPopover-buttons'>
          <Button
            kind='outlined'
            size='medium'
            onClick={() => setShowResolveForm(false)}>
            {l('Back')}
          </Button>
          <Button
            kind='filled'
            size='medium'
            onClick={handleResolve}
            isDisabled={comment.trim().length <= 2 || isResolving}
            leftIcon={<Icon kind='FlagResolved' color='gray-000' />}>
            {isResolving ? l('Saving') : l('Save')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Show new flag UI (original design)
  return (
    <Box className='FlagPopover-content'>
      {/* Header Section */}
      <Box className='FlagPopover-header'>
        <Box className='FlagPopover-headerContent'>
          <Icon kind='Flag' size='medium' className='FlagPopover-flagIcon' />
          <Box className='FlagPopover-headerText'>
            <Box className='FlagPopover-titleRow'>
              <Typography lineLimit={3} kind='h4' className='FlagPopover-title'>
                {l('Flag')}
              </Typography>
              <ButtonIcon
                kind='ghost'
                size='small'
                ariaLabel={l('close')}
                icon={<Icon kind='Close' size='small' />}
                onClick={onClose}
                className='FlagPopover-closeButton'
              />
            </Box>
            <Typography
              lineLimit={3}
              kind='body2'
              className='FlagPopover-description'>
              {l(
                'Flag this item if it appears suspicious or requires further investigation before the review can be completed.'
              )}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Form Section */}
      <Box className='FlagPopover-form'>
        <Box className='FlagPopover-commentField'>
          <Typography kind='h4'>
            <span style={{ color: 'red' }}>{REQUIRED_ASTERISK}</span>{' '}
            {l('Comments')}
          </Typography>
          <textarea
            name='flagComment'
            aria-label={l('Comments')}
            placeholder={l(
              'Describe what seems unusual or why this requires attention.'
            )}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            onKeyUp={(e) => {
              e.stopPropagation();
            }}
            required
          />
        </Box>
      </Box>

      {/* Button Section */}
      <Box className='FlagPopover-buttons'>
        <Button
          kind='outlined'
          size='medium'
          onClick={handleCancel}
          isDisabled={isSubmitting}>
          {l('Cancel')}
        </Button>
        <Button
          kind='filled'
          size='medium'
          onClick={handleSave}
          isDisabled={comment.trim().length <= 2 || isSubmitting}>
          {l('Save')}
        </Button>
      </Box>
    </Box>
  );
}

FlagPopoverContent.defaultProps = {
  onResolve: undefined,
  flagData: undefined,
  onViewAllActivities: undefined,
  initialResolve: false,
  commentOnly: false,
};

/**
 * FlagPopover Component
 * Popover for flagging audit log items with comments
 */
function FlagPopover({
  trigger,
  onSave,
  onResolve,
  isOpen,
  setIsOpen,
  flagData,
  onViewAllActivities,
  initialResolve = false,
  commentOnly = false,
}: FlagPopoverProps): React.ReactElement {
  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Popover
      placement='left'
      trigger={trigger}
      isPortalEnabled
      popoverContent={
        <div
          onBlur={() => {
            setTimeout(() => {
              if (!document.activeElement?.closest('.FlagPopover-content')) {
                handleClose();
              }
            }, 0);
          }}>
          <FlagPopoverContent
            onClose={handleClose}
            onSave={onSave}
            onResolve={onResolve}
            flagData={flagData}
            onViewAllActivities={onViewAllActivities}
            initialResolve={initialResolve}
            commentOnly={commentOnly}
          />
        </div>
      }
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
}

FlagPopover.defaultProps = {
  onResolve: undefined,
  flagData: undefined,
  onViewAllActivities: undefined,
  initialResolve: false,
  commentOnly: false,
};

export default FlagPopover;
